import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardContent,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { ApiError } from '@/lib/api/client';
import {
  useIqServiceInventory,
  useIqServiceInventoryItem,
  useCreateIqServiceInventoryItem,
  useUpdateIqServiceInventoryItem,
  useArchiveIqServiceInventoryItem,
} from '@/hooks/use-iq-service-inventory';
import { getIqServiceInventoryItem } from '@/lib/api/iq-service-inventory';
import type {
  IqServiceInventoryFilters,
  IqServiceInventoryItem,
} from '@/types/iq-service-inventory';
import { IqServiceInventoryList } from './iq-service-inventory-list';
import { IqServiceInventoryDetailDialog } from './iq-service-inventory-detail-dialog';
import {
  IqServiceInventoryUpsertDialog,
  type IqServiceUpsertValues,
} from './iq-service-inventory-upsert-dialog';
import { IqServiceInventoryArchiveDialog } from './iq-service-inventory-archive-dialog';
import { toCreateInput, toUpdateInput } from './iq-service-inventory-upsert-mapping';

const ALL = '__all__';

interface IqServiceInventoryLibraryProps {
  merchantId: string;
}

/**
 * Management surface of a merchant's IQ-service inventory (UC-1..4): server-side
 * filters, status-grouped list with cursor "load more", read-only detail, plus
 * register (UC-2), edit + optimistic-lock (UC-3) and soft-archive (UC-4).
 */
export function IqServiceInventoryLibrary({ merchantId }: IqServiceInventoryLibraryProps) {
  const { t } = useTranslation('iq-service-inventory');
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<IqServiceInventoryFilters>({});
  const [selected, setSelected] = useState<IqServiceInventoryItem | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<IqServiceInventoryItem | null>(null);
  const [archiveItem, setArchiveItem] = useState<IqServiceInventoryItem | null>(null);
  // Spec 347 §7: `?key=` deep-link. Track the consumed key so the dialog is opened
  // exactly once and never re-opens after the user closes it.
  const [consumedDeepLinkKey, setConsumedDeepLinkKey] = useState<string | null>(null);

  const query = useIqServiceInventory(merchantId, filters);
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  // Deep-link: fetch the single entry by-key (INDEPENDENT of the list's page/filter)
  // and open the detail dialog. The by-key fetch is enabled only while the param is
  // present and not yet consumed.
  const deepLinkKey = searchParams.get('key');
  const pendingDeepLinkKey =
    deepLinkKey && deepLinkKey !== consumedDeepLinkKey ? deepLinkKey : undefined;
  const deepLinkQuery = useIqServiceInventoryItem(merchantId, pendingDeepLinkKey);

  useEffect(() => {
    if (!pendingDeepLinkKey) return;
    if (deepLinkQuery.isSuccess && deepLinkQuery.data) {
      setSelected(deepLinkQuery.data);
      setConsumedDeepLinkKey(pendingDeepLinkKey);
      const next = new URLSearchParams(searchParams);
      next.delete('key');
      setSearchParams(next, { replace: true });
    } else if (deepLinkQuery.isError) {
      toast.error(t('deepLink.notFound', { key: pendingDeepLinkKey }));
      setConsumedDeepLinkKey(pendingDeepLinkKey);
      const next = new URLSearchParams(searchParams);
      next.delete('key');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDeepLinkKey, deepLinkQuery.isSuccess, deepLinkQuery.data, deepLinkQuery.isError]);

  const createMutation = useCreateIqServiceInventoryItem(merchantId);
  const updateMutation = useUpdateIqServiceInventoryItem(merchantId, editItem?.iqServiceKey ?? '');
  const archiveMutation = useArchiveIqServiceInventoryItem(merchantId);

  const setFilter = (key: keyof IqServiceInventoryFilters, value: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === ALL) delete next[key];
      else (next[key] as string) = value;
      return next;
    });
  };

  const handleCreate = async (values: IqServiceUpsertValues): Promise<boolean> => {
    await createMutation.mutateAsync(toCreateInput(values)); // rejects -> dialog shows inline error
    toast.success(t('upsert.createSuccess'));
    return true;
  };

  const handleEdit = async (values: IqServiceUpsertValues): Promise<boolean> => {
    if (!editItem) return false;
    try {
      await updateMutation.mutateAsync(toUpdateInput(values, editItem));
      toast.success(t('upsert.editSuccess'));
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Optimistic-lock conflict: reload the record so the open form carries the
        // fresh updatedAt; the user re-applies and saves again (no auto-retry). The
        // refreshed `editItem` re-prefills the dialog (its `initial` dep). Warning via
        // toast so it survives the re-prefill.
        try {
          const fresh = await getIqServiceInventoryItem(merchantId, editItem.iqServiceKey);
          setEditItem(fresh);
        } catch {
          void query.refetch();
        }
        toast.error(t('upsert.error.staleLock'));
        return false; // keep the dialog open (re-prefilled with fresh data)
      }
      throw err; // 422/400/other -> dialog shows the inline message
    }
  };

  const handleArchiveConfirm = async () => {
    if (!archiveItem) return;
    try {
      await archiveMutation.mutateAsync(archiveItem.iqServiceKey);
      toast.success(t('archive.success'));
      setArchiveItem(null);
    } catch {
      toast.error(t('archive.error'));
    }
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('register')}
        </Button>
      </div>

      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-4 py-4">
          <div className="grid gap-1.5">
            <Label htmlFor="iqinv-filter-status">{t('filters.status')}</Label>
            <Select value={filters.status ?? ALL} onValueChange={(v) => setFilter('status', v)}>
              <SelectTrigger id="iqinv-filter-status" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('filters.all')}</SelectItem>
                <SelectItem value="active">{t('status.active')}</SelectItem>
                <SelectItem value="pending_provisioning">{t('status.pending_provisioning')}</SelectItem>
                <SelectItem value="archived">{t('status.archived')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="iqinv-filter-flow">{t('filters.flowType')}</Label>
            <Select value={filters.flowType ?? ALL} onValueChange={(v) => setFilter('flowType', v)}>
              <SelectTrigger id="iqinv-filter-flow" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('filters.all')}</SelectItem>
                <SelectItem value="WAP_CLICK">{t('flowType.WAP_CLICK')}</SelectItem>
                <SelectItem value="WEB_TAN">{t('flowType.WEB_TAN')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="iqinv-filter-booking">{t('filters.bookingType')}</Label>
            <Select value={filters.bookingType ?? ALL} onValueChange={(v) => setFilter('bookingType', v)}>
              <SelectTrigger id="iqinv-filter-booking" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('filters.all')}</SelectItem>
                <SelectItem value="SUBSCRIPTION">{t('bookingType.SUBSCRIPTION')}</SelectItem>
                <SelectItem value="OTP">{t('bookingType.OTP')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {query.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : query.isError ? (
        <ApiErrorDisplay error={query.error} onRetry={() => query.refetch()} />
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {t('list.empty')}
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="mb-3 text-xs text-muted-foreground">
            {query.hasNextPage
              ? t('list.loadedMore', { count: items.length })
              : t('list.loaded', { count: items.length })}
          </p>
          <IqServiceInventoryList
            items={items}
            onSelect={setSelected}
            onEdit={setEditItem}
            onArchive={setArchiveItem}
          />
          {query.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {t('list.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}

      <IqServiceInventoryDetailDialog
        item={selected}
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      />

      <IqServiceInventoryUpsertDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        onSubmit={handleCreate}
      />

      <IqServiceInventoryUpsertDialog
        open={editItem !== null}
        onOpenChange={(open) => {
          if (!open) setEditItem(null);
        }}
        mode="edit"
        initial={editItem}
        onSubmit={handleEdit}
      />

      <IqServiceInventoryArchiveDialog
        open={archiveItem !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveItem(null);
        }}
        item={archiveItem}
        onConfirm={handleArchiveConfirm}
        isPending={archiveMutation.isPending}
      />
    </div>
  );
}
